import React from 'react';
import { Chart } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
    ChartData
} from 'chart.js';
import { useSettingsStore } from '@core/store';
import { CHART_CONFIG } from '@core/constants/chartConfig.ts';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

// Define a flexible dataset type for mixed charts
interface MixedDataset {
    label?: string;
    data: number[];
    type?: 'bar' | 'line';
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    pointStyle?: string;
    pointRadius?: number;
    pointHoverRadius?: number;
    showLine?: boolean;
    fill?: boolean;
}

interface BarChartProps {
    labels: string[];
    dataSets: MixedDataset[];
    style?: React.CSSProperties;
}

const BarChart: React.FC<BarChartProps> = ({ labels, dataSets, style }) => {
    const { darkMode } = useSettingsStore();

    // Process datasets and add colors/styling
    const processedDataSets = dataSets.map((dataSet, index) => {
        const processedSet: any = { ...dataSet };

        // Check if this is a line type dataset
        if (dataSet.type === 'line') {
            processedSet.type = 'line';
            processedSet.borderColor = CHART_CONFIG.colors[index];
            processedSet.backgroundColor = 'transparent';
            processedSet.borderWidth = 1;
            processedSet.pointStyle = 'rectRot';
            processedSet.pointRadius = 4;
            processedSet.pointHoverRadius = 6;
            processedSet.fill = false;
        } else {
            processedSet.type = 'bar';
            processedSet.backgroundColor = CHART_CONFIG.colors[index];
        }

        return processedSet;
    });

    // Calculate y-axis range safely
    const allValues = dataSets.flatMap((ds) => ds.data || []).filter((val) => typeof val === 'number');
    const yMin = allValues.length > 0 ? Math.min(...allValues) : 0;
    const yMax = allValues.length > 0 ? Math.max(...allValues) : 100;

    const options: ChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                display: true,
                labels: {
                    color: darkMode ? '#ffffff' : CHART_CONFIG.axesColor
                }
            },
            tooltip: {
                titleSpacing: 6,
                cornerRadius: 5,
                mode: 'index',
                intersect: false
            }
        },
        scales: {
            x: {
                grid: {
                    display: false,
                    color: CHART_CONFIG.chartGridColor
                },
                border: {
                    display: false
                },
                ticks: {
                    color: darkMode ? '#ffffff' : CHART_CONFIG.axesColor
                },
                display: true
            },
            y: {
                grid: {
                    display: false,
                    color: CHART_CONFIG.chartGridColor
                },
                border: {
                    display: false
                },
                ticks: {
                    color: darkMode ? '#ffffff' : CHART_CONFIG.axesColor
                },
                min: yMin - 2 > 0 ? yMin - 2 : 0,
                max: yMax + 2,
                display: true
            }
        },
        interaction: {
            intersect: false,
            mode: 'index'
        }
    };

    const data: ChartData = {
        labels,
        datasets: processedDataSets
    };

    return (
        <div style={style}>
            <Chart type="bar" data={data} options={options} />
        </div>
    );
};

export default BarChart;
